// Shared low-level plumbing used across the apiClient domain modules: auth-wrapped fetch,
// GET dedupe, base path builders, download/poll helpers, and the client-side analytics
// fallback stack used when server-side aggregate/crosstab endpoints are unavailable.
import { appConfig, getAudience } from "../../config";
import { ApiEnvelope, StudyRecord, UiFilter } from "../../types";
import type {
  AnalyticsAggregateRequest,
  AnalyticsAggregateResult,
  AnalyticsFilter,
  CrosstabResult,
  CrosstabState,
} from "../../components/analytics/types";
import { authService } from "../authService";
import { sessionActivity } from "../sessionActivity";

export const sleep = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms));

/** In-flight GET/idempotent-read dedupe: concurrent identical requests share one fetch. */
const inFlightReads = new Map<string, Promise<Response>>();

export const withAuth = async (url: string, init: RequestInit = {}, retry = true): Promise<Response> => {
  const method = String(init.method || "GET").toUpperCase();
  const canDedupe =
    method === "GET" &&
    retry &&
    (!(init.body != null && init.body !== "") || typeof init.body === "undefined");
  const dedupeKey = canDedupe ? `${method}:${url}` : null;
  if (dedupeKey) {
    const existing = inFlightReads.get(dedupeKey);
    if (existing) {
      return existing.then((response) => response.clone());
    }
  }

  const run = (async (): Promise<Response> => {
    const token = authService.getToken();
    const headers = new Headers(init.headers || {});

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    headers.set("Content-Type", "application/json");

    const response = await fetch(url, {
      ...init,
      headers
    });

    if (response.status !== 401 || !retry) {
      if (response.ok) {
        sessionActivity.notifyActivity();
      }
      return response;
    }

    const refreshed = await authService.refreshToken(token);
    if (!refreshed) {
      sessionActivity.notifyExpired();
      return response;
    }

    headers.set("Authorization", `Bearer ${refreshed}`);
    const retried = await fetch(url, { ...init, headers });
    if (retried.ok) {
      sessionActivity.notifyActivity();
    } else if (retried.status === 401) {
      sessionActivity.notifyExpired();
    }
    return retried;
  })();

  if (dedupeKey) {
    inFlightReads.set(dedupeKey, run);
    try {
      return (await run).clone();
    } finally {
      if (inFlightReads.get(dedupeKey) === run) {
        inFlightReads.delete(dedupeKey);
      }
    }
  }

  return run;
};

export const baseStudiesPath = (): string => {
  return `${appConfig.apiBaseUrl}/${getAudience()}/studies`;
};

export const basePanelistsPath = (): string => {
  return `${appConfig.apiBaseUrl}/${getAudience()}/panelists`;
};

export const baseCrosstabsPath = (): string => {
  return `${appConfig.apiBaseUrl}/${getAudience()}/crosstabs`;
};

export const baseCrossStudyChatPath = (analysisId: string): string => {
  return `${appConfig.apiBaseUrl}/${getAudience()}/insights/cross-study-analysis/${encodeURIComponent(
    analysisId
  )}/chat/conversations`;
};

export const baseDashboardsPath = (): string => {
  return `${appConfig.apiBaseUrl}/${getAudience()}/dashboards`;
};

export const baseDataExplorerViewsPath = (): string => {
  return `${appConfig.apiBaseUrl}/${getAudience()}/data-explorer/views`;
};

export const baseDesignSystemPath = (): string => {
  return `${appConfig.apiBaseUrl}/${getAudience()}/design-system`;
};

export const downloadBase64File = (content_base64: string, filename: string, content_type: string, fallbackName: string): void => {
  const byteChars = atob(content_base64);
  const byteArray = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteArray[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([byteArray], { type: content_type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || fallbackName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const baseExportJobsPath = (): string => {
  return `${appConfig.apiBaseUrl}/${getAudience()}/export-jobs`;
};

const EXPORT_JOB_POLL_INTERVAL_MS = 2500;
const EXPORT_JOB_MAX_WAIT_MS = 300_000;

export const pollExportJobUntilComplete = async (
  jobId: string,
  setLabel?: (label: string) => void
): Promise<{ content_base64: string; filename: string; content_type: string }> => {
  const deadline = Date.now() + EXPORT_JOB_MAX_WAIT_MS;
  let elapsed = 0;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, EXPORT_JOB_POLL_INTERVAL_MS));
    elapsed += EXPORT_JOB_POLL_INTERVAL_MS;
    if (setLabel) {
      setLabel(`Generating… (${Math.round(elapsed / 1000)}s)`);
    }
    const response = await withAuth(`${baseExportJobsPath()}/${encodeURIComponent(jobId)}`, { method: "GET" });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Export job poll failed (${response.status}) ${text}`);
    }
    const envelope = (await response.json()) as ApiEnvelope<{
      job_id: string;
      status: string;
      content_base64?: string;
      filename?: string;
      content_type?: string;
      error_message?: string;
    }>;
    const job = envelope.data;
    if (job.status === "completed") {
      return {
        content_base64: job.content_base64!,
        filename: job.filename!,
        content_type: job.content_type!,
      };
    }
    if (job.status === "failed") {
      throw new Error(job.error_message || "Export failed");
    }
  }
  throw new Error("Export timed out waiting for result");
};

const CROSSTAB_ANALYTICS_POLL_INTERVAL_MS = 1500;
// Matches the crosstabAnalyticsWorker Lambda's timeout (900s, the AWS Lambda
// hard cap) -- the client must not give up before the worker could still be
// legitimately running.
const CROSSTAB_ANALYTICS_MAX_WAIT_MS = 900_000;

export type CrosstabJobProgress = {
  status: string;
  rows_completed?: number | null;
  rows_total?: number | null;
};

export const pollCrosstabAnalyticsJobUntilComplete = async <T = CrosstabResult>(
  studyId: string,
  jobId: string,
  mapResult: (raw: unknown) => T = (raw) => sanitizeCrosstabResult(raw as CrosstabResult) as unknown as T,
  onProgress?: (job: CrosstabJobProgress) => void
): Promise<T> => {
  const deadline = Date.now() + CROSSTAB_ANALYTICS_MAX_WAIT_MS;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, CROSSTAB_ANALYTICS_POLL_INTERVAL_MS));
    const response = await withAuth(
      `${baseStudiesPath()}/${studyId}/analytics/crosstab/${encodeURIComponent(jobId)}/status`,
      { method: "GET" }
    );
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Crosstab analytics poll failed (${response.status}) ${text}`);
    }
    const envelope = (await response.json()) as ApiEnvelope<{
      job_id: string;
      status: string;
      result?: unknown;
      error_message?: string;
      rows_completed?: number | null;
      rows_total?: number | null;
    }>;
    const job = envelope.data;
    onProgress?.(job);
    if (job.status === "completed") {
      return mapResult(job.result);
    }
    if (job.status === "failed") {
      throw new Error(job.error_message || "Crosstab generation failed");
    }
  }
  throw new Error("Crosstab generation timed out waiting for result");
};

export const analyticsFilterToUiFilter = (filter: AnalyticsFilter): UiFilter => ({
  fieldName: filter.variable,
  fieldType: "text",
  operator: filter.operator,
  value: Array.isArray(filter.value) ? filter.value.join(",") : String(filter.value),
  loopValue: filter.loopValue
});

export const readRecordValue = (
  record: StudyRecord,
  fieldName: string
): string | number | boolean | Array<string | number | boolean> | null => {
  if (fieldName === "record_id") {
    return record.record_id;
  }
  const value = record.fields[fieldName];
  return value == null ? null : value;
};

export const toNumber = (value: unknown): number | null => {
  if (value == null || value === "" || isNonAnswerValue(value)) {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const isNonAnswerValue = (value: unknown): boolean => {
  if (value == null || value === "") {
    return false;
  }
  const numeric = Number(String(value).trim());
  return Number.isFinite(numeric) ? numeric === -1 : String(value).trim() === "-1";
};

export const loadAllResponsesForAnalytics = async (studyId: string, filters: AnalyticsFilter[] = []): Promise<StudyRecord[]> => {
  const rows: StudyRecord[] = [];
  let cursor: string | undefined;
  let guard = 0;
  do {
    const params = new URLSearchParams({
      limit: "100",
      sort_by: "record_id",
      sort_order: "asc"
    });
    if (cursor) {
      params.set("cursor", cursor);
    }
    filters.map(analyticsFilterToUiFilter).forEach((f) => {
      params.append(`filter.${f.fieldName}`, `${f.operator}:${f.value}`);
      if (f.loopValue) {
        params.append(`filter_loop_value.${f.fieldName}`, f.loopValue);
      }
    });
    const response = await withAuth(`${baseStudiesPath()}/${studyId}/responses?${params.toString()}`, { method: "GET" });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to get responses for analytics fallback (${response.status}) ${text}`);
    }
    const body = (await response.json()) as ApiEnvelope<StudyRecord[]>;
    rows.push(...(body.data || []));
    cursor = body.meta?.next_cursor || undefined;
    guard += 1;
  } while (cursor && guard < 100);
  return rows;
};

// Orders scale-like values so numeric codes sort naturally (1, 2, …, 10) and
// labelled scale points sort by the first number found anywhere in the label
// ("Neutral 5" -> 5, "Extremely Affordable 10" -> 10, "1 - Not at all likely" ->
// 1). Falls back to numeric-aware string compare, which avoids the lexicographic
// ordering bug where "10" lands right after "1".
const scaleSortNumber = (value: unknown): number | null => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  const direct = Number(text);
  if (!Number.isNaN(direct)) return direct;
  const match = text.match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
};

export const compareValueKeys = (a: unknown, b: unknown): number => {
  const na = scaleSortNumber(a);
  const nb = scaleSortNumber(b);
  if (na !== null && nb !== null) {
    if (na !== nb) return na - nb;
  } else if (na !== null) {
    return -1;
  } else if (nb !== null) {
    return 1;
  }
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true, sensitivity: "base" });
};

const compareValueCombos = (a: string[], b: string[]): number => {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i += 1) {
    const cmp = compareValueKeys(a[i] ?? "", b[i] ?? "");
    if (cmp) return cmp;
  }
  return 0;
};

export const aggregateFromResponses = (studyId: string, payload: AnalyticsAggregateRequest, records: StudyRecord[]): AnalyticsAggregateResult => {
  const measure = payload.measure || "count";
  const groupBy = payload.groupBy || [];
  const groups = new Map<string, { key: Record<string, string | number | boolean | null>; rows: StudyRecord[] }>();
  const keyFor = (record: StudyRecord) => {
    if (!groupBy.length) {
      return "__all__";
    }
    return groupBy.map((field) => String(readRecordValue(record, field) ?? "")).join("\u001f");
  };
  records.forEach((record) => {
    if (groupBy.some((field) => isNonAnswerValue(readRecordValue(record, field)))) {
      return;
    }
    const key = keyFor(record);
    const existing = groups.get(key);
    if (existing) {
      existing.rows.push(record);
      return;
    }
    const keyObject: Record<string, string | number | boolean | null> = {};
    groupBy.forEach((field) => {
      const raw = readRecordValue(record, field);
      keyObject[field] = raw == null ? "" : String(raw).trim();
    });
    groups.set(key, { key: keyObject, rows: [record] });
  });

  // Legacy empty Metric Source → CAST(1) for mean/sum/min/max/nps.
  const numericValues = (groupRows: StudyRecord[]): number[] => {
    if (!payload.variable) {
      return groupRows.map(() => 1);
    }
    return groupRows
      .map((record) => toNumber(readRecordValue(record, payload.variable || "")))
      .filter((value): value is number => value != null);
  };

  const rows = Array.from(groups.values()).map((group) => {
    const label = groupBy.length ? groupBy.map((field) => String(group.key[field] ?? "Blank")).join(" / ") : "All responses";
    const values = numericValues(group.rows);
    let value: number | null = group.rows.length;
    let base = group.rows.length;
    if (measure === "mean") {
      value = values.length ? Number((values.reduce((sum, item) => sum + item, 0) / values.length).toFixed(4)) : null;
      base = values.length;
    } else if (measure === "sum") {
      value = values.length ? Number(values.reduce((sum, item) => sum + item, 0).toFixed(4)) : null;
      base = values.length;
    } else if (measure === "min") {
      value = values.length ? Math.min(...values) : null;
      base = values.length;
    } else if (measure === "max") {
      value = values.length ? Math.max(...values) : null;
      base = values.length;
    } else if (measure === "nps") {
      base = values.length;
      if (base) {
        const highMin = payload.scoreBands?.highMin ?? 9;
        const lowMax = payload.scoreBands?.lowMax ?? 6;
        const promoters = values.filter((item) => item >= highMin).length;
        const detractors = values.filter((item) => item <= lowMax).length;
        value = Number((((promoters - detractors) / base) * 100).toFixed(4));
      } else {
        value = null;
      }
    }
    return {
      key: group.key,
      label,
      value,
      base,
      percent: records.length ? Number(((group.rows.length / records.length) * 100).toFixed(4)) : 0
    };
  });
  if (groupBy.length) {
    rows.sort((left, right) => {
      for (const field of groupBy) {
        const cmp = compareValueKeys(left.key[field], right.key[field]);
        if (cmp) return cmp;
      }
      return 0;
    });
  }
  return { study_definition_id: studyId, measure, variable: payload.variable || null, groupBy, total: records.length, rows };
};

const CROSSTAB_COMBO_SEP = "\u241f";

export const crosstabFromResponses = (studyId: string, payload: CrosstabState, records: StudyRecord[]): CrosstabResult => {
  const rowVariable = payload.rows[0] || "record_id";
  const colFields = Array.from(new Set((payload.columns || []).map((field) => String(field || "").trim()).filter(Boolean)));
  const validRecords = records.filter(
    (record) =>
      !isNonAnswerValue(readRecordValue(record, rowVariable)) &&
      colFields.every((field) => !isNonAnswerValue(readRecordValue(record, field)))
  );
  const comboValues = (record: StudyRecord): string[] => colFields.map((field) => String(readRecordValue(record, field) ?? ""));
  const comboKey = (parts: string[]): string => parts.join(CROSSTAB_COMBO_SEP);

  const rowKeys = Array.from(new Set(validRecords.map((record) => readRecordValue(record, rowVariable)))).sort((a, b) =>
    compareValueKeys(a, b)
  );

  const comboByKey = new Map<string, string[]>();
  validRecords.forEach((record) => {
    if (!colFields.length) return;
    const parts = comboValues(record);
    comboByKey.set(comboKey(parts), parts);
  });
  const leafCombos = Array.from(comboByKey.values()).sort((a, b) => compareValueCombos(a, b));
  const leafKeys = leafCombos.map((parts) => comboKey(parts));
  const columnKeys = [...leafKeys, "__total__"];

  const columnLabels = columnKeys.reduce<Record<string, string>>((acc, key) => {
    if (key === "__total__") {
      acc[key] = "Total";
    } else {
      const parts = comboByKey.get(key) || [key];
      acc[key] = parts.map((part) => part || "Blank").join(" / ");
    }
    return acc;
  }, {});

  const matchesLeaf = (record: StudyRecord, key: string): boolean => {
    if (key === "__total__" || !colFields.length) return true;
    return comboKey(comboValues(record)) === key;
  };

  const totalBase = validRecords.length;
  const colBase = (key: string) => (key === "__total__" || !colFields.length ? totalBase : validRecords.filter((record) => matchesLeaf(record, key)).length);

  const grandTotal = leafKeys.reduce((sum, key) => sum + colBase(key), 0) || totalBase;

  const sigEnabled = Boolean(payload.significance?.enabled || payload.statistics.includes("sigTest"));
  const confidenceLevel = payload.significance?.confidenceLevel ?? 0.9;
  const criticalZ = confidenceLevel >= 0.99 ? 2.576 : confidenceLevel >= 0.95 ? 1.96 : 1.645;

  const cellSignificance = (observed: number, rowBase: number, colBaseN: number): "higher" | "lower" | null => {
    const otherColBase = grandTotal - colBaseN;
    if (!rowBase || !colBaseN || !otherColBase || !grandTotal) return null;
    const p1 = observed / colBaseN;
    const p2 = (rowBase - observed) / otherColBase;
    const pPooled = rowBase / grandTotal;
    const variance = pPooled * (1 - pPooled) * (1 / colBaseN + 1 / otherColBase);
    if (variance <= 0) return null;
    const z = (p1 - p2) / Math.sqrt(variance);
    if (z >= criticalZ) return "higher";
    if (z <= -criticalZ) return "lower";
    return null;
  };

  const rows = rowKeys.map((rowKey) => {
    const rowMatches = validRecords.filter((record) => String(readRecordValue(record, rowVariable) ?? "") === String(rowKey ?? ""));
    const rowBase = rowMatches.length;
    const cells = columnKeys.reduce<CrosstabResult["rows"][number]["cells"]>((acc, key) => {
      const matching = rowMatches.filter((record) => matchesLeaf(record, key));
      const base = colBase(key);
      const count = matching.length;
      const cell: CrosstabResult["rows"][number]["cells"][string] = {
        count,
        base,
        columnPercent: base ? Number(((count / base) * 100).toFixed(payload.display.decimalPlaces)) : 0,
        rowPercent: rowBase ? Number(((count / rowBase) * 100).toFixed(payload.display.decimalPlaces)) : 0,
        totalPercent: totalBase ? Number(((count / totalBase) * 100).toFixed(payload.display.decimalPlaces)) : 0
      };
      if (sigEnabled && key !== "__total__" && colFields.length) {
        const direction = cellSignificance(count, rowBase, base);
        if (direction) cell.significance = direction;
      }
      acc[key] = cell;
      return acc;
    }, {});
    return { key: rowKey, label: String(rowKey ?? "Blank"), cells };
  });

  const buildColNodes = (combos: string[][], depth: number): CrosstabResult["columnHeader"] => {
    const grouped = new Map<string, string[][]>();
    combos.forEach((combo) => {
      const value = combo[depth] ?? "";
      const bucket = grouped.get(value) || [];
      bucket.push(combo);
      grouped.set(value, bucket);
    });
    return Array.from(grouped.entries()).map(([value, group]) => {
      if (depth + 1 < colFields.length) {
        const children = buildColNodes(group, depth + 1);
        return {
          label: value || "Blank",
          field: colFields[depth],
          span: (children || []).reduce((sum, child) => sum + child.span, 0),
          children
        };
      }
      return { label: value || "Blank", field: colFields[depth], key: comboKey(group[0]), span: 1 };
    });
  };
  const columnHeader = colFields.length ? buildColNodes(leafCombos, 0) : [];

  return {
    study_definition_id: studyId,
    rowVariable,
    columnVariable: colFields[0] || null,
    columnVariables: colFields,
    columnFields: colFields,
    columnHeader,
    rowKeys,
    columnKeys,
    columnLabels,
    rows,
    totalBase,
    statistics: payload.statistics,
    significance: { enabled: sigEnabled, confidenceLevel }
  };
};

export const sanitizeAggregateResult = (result: AnalyticsAggregateResult): AnalyticsAggregateResult => {
  if (!result.groupBy.length) {
    return result;
  }
  const rows = result.rows.filter((row) => !Object.values(row.key).some(isNonAnswerValue) && !isNonAnswerValue(row.label));
  return { ...result, rows, total: rows.reduce((sum, row) => sum + row.base, 0) };
};

export const sanitizeCrosstabResult = (result: CrosstabResult): CrosstabResult => {
  const columnKeys = result.columnKeys.filter((key) => key === "__total__" || (!isNonAnswerValue(key) && !isNonAnswerValue(result.columnLabels[key])));
  const rows = result.rows
    .filter((row) => !isNonAnswerValue(row.key) && !isNonAnswerValue(row.label))
    .map((row) => ({
      ...row,
      cells: columnKeys.reduce<CrosstabResult["rows"][number]["cells"]>((cells, key) => {
        if (row.cells[key]) {
          cells[key] = row.cells[key];
        }
        return cells;
      }, {})
    }));
  const totalBase = rows.reduce((sum, row) => sum + (row.cells.__total__?.count || 0), 0);
  return { ...result, columnKeys, rows, totalBase };
};

export const sanitizeCrosstabBatchResult = (raw: unknown): Record<string, CrosstabResult> => {
  const entries = Object.entries((raw as Record<string, CrosstabResult>) || {});
  return Object.fromEntries(entries.map(([id, result]) => [id, sanitizeCrosstabResult(result)]));
};
